// Lib
import React, { useEffect } from 'react';

// Utils
import { calculateClipperLocationState } from './cardClipperUtils';
import getSelectionRect from '../../../utils/dom/getSelectionRect';
import isVisibleSelection from '../../../../common/tiptap/utils/selection/isVisibleSelection';

// Context
import { useZoomContext } from '../../../canvas/zoom/ZoomStateContext';

// Types
import { NodeSelection, Selection } from '@tiptap/pm/state';
import { TiptapNodeType } from '../../../../common/tiptap/tiptapTypes';

const isOnlySelectingHR = (selection: Selection): boolean => {
    if (!selection || selection.to - selection.from !== 1) return false;
    if (!(selection instanceof NodeSelection)) return false;

    return selection.node.type.name === TiptapNodeType.horizontalRule;
};

/**
 * Determines where the card clipper should be positioned based on the current selection
 * and the container it is being rendered in.
 */
export const useCardClipperPositioner = (
    currentSelection: Selection | undefined,
    clipperContainerRef: React.RefObject<HTMLDivElement>,
    isClipping: boolean,
) => {
    const { getContextZoomScale } = useZoomContext();

    const [selectionYMidpoint, setSelectionYMidpoint] = React.useState<number>(0);
    const [selectionHeight, setSelectionHeight] = React.useState<number>(0);

    useEffect(() => {
        if (isClipping) return;
        if (!currentSelection) return;

        if (!isVisibleSelection(currentSelection)) return;
        if (isOnlySelectingHR(currentSelection)) return;

        const zoomScale = getContextZoomScale();

        const selectionRect = getSelectionRect();
        const containerRect = clipperContainerRef.current?.getBoundingClientRect();

        const state = calculateClipperLocationState(selectionRect, containerRect, zoomScale);

        setSelectionYMidpoint(state?.selectionYMidpoint || 0);
        setSelectionHeight(state?.selectionHeight || 0);
    }, [currentSelection, clipperContainerRef, isClipping]);

    return {
        selectionYMidpoint,
        selectionHeight,
    };
};
